import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import { confirmUngeocodedMapAddressStandaloneIfNeeded } from '../../utils/mapSubmitGuard';
import PDFView from './PDFView';
import SuccessPopup from '../shared/SuccessPopup';
import './StudentFormsModal.css';

const StudentFormsModal = ({ student, onClose }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [householdAddresses, setHouseholdAddresses] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'view', 'edit', 'pdf'
  const [editFormData, setEditFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [schoolLunchHistory, setSchoolLunchHistory] = useState(null);
  const [migrantQuestionnaires, setMigrantQuestionnaires] = useState(null);

  useEffect(() => {
    if (student?.id) {
      fetchStudentForms();
      fetchHouseholdAddresses();
      fetchSchoolLunchHistory();
      fetchMigrantQuestionnaires();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  const fetchSchoolLunchHistory = async () => {
    if (!student?.id) return;
    try {
      // Household history, not history-by-intake. The per-child endpoint matches submissions
      // whose children[] contains this student's exact name, which hides the versions that
      // matter most when checking a correction: one that OMITTED this child, or one where the
      // name was typed differently. One real household had 8 applications on file with each of
      // its students seeing only 1.
      const res = await axios.get(`/api/forms/school-lunch-application/history-by-household/${student.id}`);
      setSchoolLunchHistory(res.data?.submissions || []);
    } catch (e) {
      console.warn('School lunch history:', e.response?.data || e.message);
      setSchoolLunchHistory([]);
    }
  };

  const fetchMigrantQuestionnaires = async () => {
    if (!student?.id) return;
    try {
      const res = await axios.get(`/api/migrant-questionnaire/by-intake/${student.id}`);
      setMigrantQuestionnaires(res.data?.submissions || []);
    } catch (e) {
      console.warn('Migrant questionnaire:', e.response?.data || e.message);
      setMigrantQuestionnaires([]);
    }
  };

  const openMigrantPdf = async (submissionId) => {
    try {
      const res = await axios.post('/api/migrant-questionnaire/generate-pdf',
        { submissionId }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not open questionnaire PDF');
    }
  };

  const openSchoolLunchPdf = async (submissionId) => {
    try {
      const res = await axios.get(`/api/forms/school-lunch-application/pdf/${submissionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not open PDF');
    }
  };

  /**
   * The household's addresses. Its own request, because /district/:id/forms responds with a
   * bare array and could not carry it without changing that shape.
   *
   * Failure is silent: this is supplementary context, and the forms list must still render.
   */
  const fetchHouseholdAddresses = async () => {
    try {
      const res = await axios.get(`/api/student-intake/district/${student.id}/household-address`);
      setHouseholdAddresses(res.data?.addresses || null);
    } catch (error) {
      setHouseholdAddresses(null);
    }
  };

  const fetchStudentForms = async () => {
    try {
      setLoading(true);
      console.log('Fetching forms for student intake:', student.id);
      const response = await axios.get(`/api/student-intake/district/${student.id}/forms`);
      console.log('Forms response:', response.data);
      // Debug: Check field counts for each form
      console.log('=== FORM FIELD COUNT DEBUG ===');
      response.data.forEach((form, index) => {
        const fieldCount = form.form_data ? Object.keys(form.form_data).length : 0;
        console.log(`Form ${index}: ${form.instance_name || form.master_form_name} - ${fieldCount} fields`);
        if (fieldCount === 21) {
          console.log('🚨 ALERT: This form has exactly 21 fields!');
          console.log('Form data keys:', Object.keys(form.form_data));
        }
        if (fieldCount < 21) {
          console.log('✅ Normal field count for:', form.instance_name || form.master_form_name);
        }
      });
      console.log('=== END FORM FIELD COUNT DEBUG ===');
      setForms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch student forms:', error);
      console.error('Error details:', error.response?.data);
      
      // Handle 404 (student not found) vs 500 (server error) differently
      if (error.response?.status === 404) {
        // Student not found - this shouldn't happen if we got here, but handle gracefully
        setForms([]);
      } else if (error.response?.status === 500) {
        // Server error - show error message but don't crash
        console.error('Server error fetching forms:', error.response?.data);
        setForms([]);
        // Don't show alert - we'll show a message in the UI instead
      } else {
        // Other errors
        setForms([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewForm = async (formId) => {
    try {
      const response = await axios.get(`/api/form-submissions/student-form/${formId}`);
      setSelectedForm(response.data);
      setViewMode('view');

      // Check if this is a PDF form (like School Lunch Application)
      const isPDFForm = response.data.master_form_name === 'School Lunch Application' ||
                       response.data.instance_name?.includes('PDF') ||
                       (response.data.form_data && response.data.form_data.form_type === 'family');

      if (isPDFForm) {
        const rawFormValues = response.data.form_data || {};
        const amendmentMetadata =
          rawFormValues && typeof rawFormValues === 'object' && !Array.isArray(rawFormValues)
            ? (rawFormValues.__student_info_amendments || {})
            : {};
        const visibleFormValues =
          rawFormValues && typeof rawFormValues === 'object' && !Array.isArray(rawFormValues)
            ? { ...rawFormValues }
            : rawFormValues;
        if (visibleFormValues && typeof visibleFormValues === 'object' && !Array.isArray(visibleFormValues)) {
          delete visibleFormValues.__student_info_amendments;
        }

        // For PDF forms, don't try to render as a form - just show the data
        setEditFormData({
          formDefinition: null, // No form definition for PDF forms
          formValues: visibleFormValues,
          amendmentMetadata
        });
        setViewMode('pdf'); // Use PDF view mode for PDF forms
      } else {
        // Parse form instance data for regular forms
        let instanceFormData = response.data.instance_form_data;
        if (typeof instanceFormData === 'string') {
          instanceFormData = JSON.parse(instanceFormData);
        }
        console.log('Instance form data sections:', instanceFormData?.sections?.length);
        instanceFormData?.sections?.forEach((section, index) => {
          console.log(`Section ${index}: ${section.fields?.length || 0} fields`);
        });
        const totalFields = instanceFormData?.sections?.reduce((sum, section) => sum + (section.fields?.length || 0), 0) || 0;
        console.log('Total fields in form definition:', totalFields);
        console.log('Form data keys:', Object.keys(response.data.form_data));

        const rawFormValues = response.data.form_data || {};
        const amendmentMetadata =
          rawFormValues && typeof rawFormValues === 'object' && !Array.isArray(rawFormValues)
            ? (rawFormValues.__student_info_amendments || {})
            : {};
        const visibleFormValues =
          rawFormValues && typeof rawFormValues === 'object' && !Array.isArray(rawFormValues)
            ? { ...rawFormValues }
            : rawFormValues;
        if (visibleFormValues && typeof visibleFormValues === 'object' && !Array.isArray(visibleFormValues)) {
          delete visibleFormValues.__student_info_amendments;
        }

        setEditFormData({
          formDefinition: instanceFormData,
          formValues: visibleFormValues,
          amendmentMetadata
        });
      }
    } catch (error) {
      console.error('Failed to fetch form:', error);
      setActionFeedback({
        variant: 'error',
        title: 'Load failed',
        message: 'Could not load this form. Please try again.'
      });
    }
  };

  const handleEditForm = () => {
    setViewMode('edit');
  };

  const handleSaveEdit = async () => {
    try {
      if (
        editFormData?.formDefinition &&
        !confirmUngeocodedMapAddressStandaloneIfNeeded(
          editFormData.formDefinition,
          editFormData.formValues,
          selectedForm
        )
      ) {
        return;
      }
      setSaving(true);
      console.log('Saving form data:', editFormData.formValues);
      console.log('Field count:', Object.keys(editFormData.formValues).length);
      const payload = { ...(editFormData.formValues || {}) };
      if (editFormData?.amendmentMetadata && Object.keys(editFormData.amendmentMetadata).length > 0) {
        payload.__student_info_amendments = editFormData.amendmentMetadata;
      }
      await axios.put(`/api/form-submissions/student-form/${selectedForm.id}`, {
        form_data: payload
      });
      setViewMode('view');
      fetchStudentForms(); // Refresh list
      setActionFeedback({
        variant: 'success',
        title: 'Saved',
        message: 'Form was updated successfully.'
      });
    } catch (error) {
      console.error('Failed to update form:', error);
      setActionFeedback({
        variant: 'error',
        title: 'Save failed',
        message: error.response?.data?.error || 'Could not update the form.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setViewMode('view');
    // Reset form data to original
    if (selectedForm) {
      const originalFormValues = selectedForm.form_data || {};
      const amendmentMetadata =
        originalFormValues && typeof originalFormValues === 'object' && !Array.isArray(originalFormValues)
          ? (originalFormValues.__student_info_amendments || {})
          : {};
      const visibleFormValues =
        originalFormValues && typeof originalFormValues === 'object' && !Array.isArray(originalFormValues)
          ? { ...originalFormValues }
          : originalFormValues;
      if (visibleFormValues && typeof visibleFormValues === 'object' && !Array.isArray(visibleFormValues)) {
        delete visibleFormValues.__student_info_amendments;
      }
      setEditFormData({
        formDefinition: editFormData.formDefinition,
        formValues: visibleFormValues,
        amendmentMetadata
      });
    }
  };

  const handleDeleteForm = async (formId) => {
    try {
      await axios.delete(`/api/form-submissions/student-form/${formId}`);
      setShowDeleteConfirm(null);
      fetchStudentForms(); // Refresh list
      if (selectedForm?.id === formId) {
        setSelectedForm(null);
        setViewMode(null);
      }
      setActionFeedback({
        variant: 'success',
        title: 'Deleted',
        message: 'The form was moved to trash.'
      });
    } catch (error) {
      console.error('Failed to delete form:', error);
      setActionFeedback({
        variant: 'error',
        title: 'Delete failed',
        message: error.response?.data?.error || 'Could not delete this form.'
      });
    }
  };

  const handleViewPDF = () => {
    setViewMode('pdf');
  };

  const handleFormChange = (newValues) => {
    setEditFormData({
      ...editFormData,
      formValues: newValues
    });
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal student-forms-modal large-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Student Forms - {student?.first_name} {student?.last_name}</h3>
            <button onClick={onClose} className="btn-close">&times;</button>
          </div>
          <div className="modal-body">
            <div>Loading forms...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal student-forms-modal large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Student Forms - {student?.first_name} {student?.last_name}</h3>
          <button onClick={onClose} className="btn-close">&times;</button>
        </div>
        <div className="modal-body">
          {!selectedForm ? (
            <>
              {/* Household addresses. The form's own address question is labelled "Current
                  Address" and nothing said "mailing address", so staff could not tell which one
                  they were looking at - and the mailing address the parent gave in Household
                  Information was not shown anywhere on this side. */}
              <div
                style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}
              >
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Household addresses</h4>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#5a6b7b' }}>
                  From the parent&apos;s Household Information, not from a form. Blank means the
                  parent has not supplied it.
                </p>
                {!householdAddresses ? (
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#777' }}>
                    No household address on file for this parent.
                  </p>
                ) : (
                  <dl style={{ margin: 0, fontSize: '0.9rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                      <dt style={{ flex: '0 0 40%', color: '#5a6b7b' }}>Mailing address</dt>
                      <dd style={{ margin: 0, fontWeight: 600 }}>
                        {householdAddresses.mailingOneLine || '—'}
                        {householdAddresses.mailing?.hasAny && householdAddresses.sameAsResidence && (
                          <span style={{ fontWeight: 400, color: '#5a6b7b' }}> (same as current address)</span>
                        )}
                      </dd>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                      <dt style={{ flex: '0 0 40%', color: '#5a6b7b' }}>Current address on file</dt>
                      <dd style={{ margin: 0, fontWeight: 600 }}>
                        {householdAddresses.residenceOneLine || '—'}
                      </dd>
                    </div>
                    {householdAddresses.mailing?.county && (
                      <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                        <dt style={{ flex: '0 0 40%', color: '#5a6b7b' }}>Mailing county</dt>
                        <dd style={{ margin: 0 }}>{householdAddresses.mailing.county}</dd>
                      </div>
                    )}
                    {householdAddresses.residence?.county && (
                      <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                        <dt style={{ flex: '0 0 40%', color: '#5a6b7b' }}>Current address county</dt>
                        <dd style={{ margin: 0 }}>{householdAddresses.residence.county}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>

              {/* DESE Title I.C migrant questionnaire. Shown per student because the
                  state requires one per student, and staff need the answers to know
                  whether a migrant-program referral is needed. */}
              {migrantQuestionnaires && migrantQuestionnaires.length > 0 && (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>Migrant questionnaire (Title I.C)</h4>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#555' }}>
                    Required by DESE for every student. Answers are shown in the language the parent used.
                    A “needs follow-up” flag means at least one answer was yes, so migrant program staff
                    should be notified. PDF is the official MO 500-3129 and opens in a new tab.
                  </p>
                  {/* Stacked rather than tabular: this modal is only ~535px wide, and a
                      wide table clipped the follow-up flag and the PDF button off-screen. */}
                  {migrantQuestionnaires.map((row) => {
                    const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');
                    const work = [...row.jobTypeLabels, row.jobTypesOther].filter(Boolean);
                    const langName = { en: 'English', es: 'Español', fr: 'Français' }[row.language] || row.language;
                    return (
                      <div
                        key={row.submissionId}
                        style={{
                          background: '#fff',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          padding: '0.75rem',
                          marginBottom: '0.5rem'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.9rem', color: '#555' }}>
                            {row.submittedAt ? new Date(row.submittedAt).toLocaleString() : '—'} · {langName}
                          </span>
                          {row.needsFollowUp && (
                            <span style={{
                              background: '#fff3cd', border: '1px solid #ffe69c', color: '#8a5a00',
                              borderRadius: '3px', padding: '0.1rem 0.45rem', fontSize: '0.8rem', fontWeight: 600
                            }}>
                              Needs follow-up
                            </span>
                          )}
                        </div>
                        <dl style={{ margin: 0, fontSize: '0.9rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                            <dt style={{ flex: '0 0 55%', color: '#5a6b7b' }}>Moved into district (3 yrs)</dt>
                            <dd style={{ margin: 0, fontWeight: 600 }}>{yn(row.movedIntoDistrict)}</dd>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                            <dt style={{ flex: '0 0 55%', color: '#5a6b7b' }}>Agriculture / fishing work</dt>
                            <dd style={{ margin: 0, fontWeight: 600 }}>{yn(row.workedAgFishing)}</dd>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.15rem 0' }}>
                            <dt style={{ flex: '0 0 55%', color: '#5a6b7b' }}>Plans such work soon</dt>
                            <dd style={{ margin: 0, fontWeight: 600 }}>{yn(row.plansAgFishing)}</dd>
                          </div>
                          <div style={{ padding: '0.35rem 0 0' }}>
                            <dt style={{ color: '#5a6b7b' }}>Work reported</dt>
                            <dd style={{ margin: '0.15rem 0 0', whiteSpace: 'normal' }}>
                              {work.length ? work.join('; ') : '—'}
                            </dd>
                          </div>
                        </dl>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ marginTop: '0.6rem' }}
                          onClick={() => openMigrantPdf(row.submissionId)}
                        >
                          View PDF
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {schoolLunchHistory && schoolLunchHistory.length > 0 && (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #dee2e6'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>School lunch (household)</h4>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#555' }}>
                    Every application this family has filed, newest first. Applications are per
                    household, so some may not name this student. Correcting an application
                    creates a new one rather than editing the old, so the earlier versions stay
                    here. <strong>Current</strong> is the one the reports list shows. PDF opens
                    in a new tab.
                  </p>
                  <table className="data-table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>Submitted</th>
                        <th>Enr. year</th>
                        <th>Household</th>
                        <th>Children named</th>
                        <th>Printed</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schoolLunchHistory.map((row) => {
                        // Whether this version actually names the student whose record we are in.
                        // A version that left them off is exactly what staff need to spot.
                        const thisStudent = `${(student.first_name || '').trim()} ${(student.last_name || '').trim()}`.trim().toLowerCase();
                        const named = String(row.children_named || '').toLowerCase();
                        const includesThisStudent = thisStudent !== '' && named.includes(thisStudent);
                        return (
                          <tr key={row.id} style={row.is_current ? { background: '#f0f7ff' } : undefined}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {/* Compact date. toLocaleString() ("8/27/2026, 2:39:19 PM") pushed
                                  the columns that matter - Children named, Printed, View PDF -
                                  off the right edge of the modal behind a scrollbar. Seconds are
                                  noise here; the day and time distinguish versions fine. */}
                              {row.submitted_at
                                ? new Date(row.submitted_at).toLocaleString(undefined, {
                                  month: 'numeric', day: 'numeric', year: '2-digit',
                                  hour: 'numeric', minute: '2-digit'
                                })
                                : '—'}
                              {row.is_current && (
                                <span style={{
                                  marginLeft: '0.4rem', fontSize: '0.75rem', fontWeight: 600,
                                  color: '#1a6fb5', border: '1px solid #a8cdea',
                                  borderRadius: '3px', padding: '0.05rem 0.3rem'
                                }}>Current</span>
                              )}
                            </td>
                            <td>{row.enrollment_school_year || '—'}</td>
                            <td>
                              {row.total_household_members || '—'}
                              <span style={{ color: '#888', fontSize: '0.85rem' }}>
                                {/* Number(): Postgres count(*) is a bigint, which node-postgres
                                    returns as a STRING, so a === 1 check never matched and one
                                    adult rendered as "1 adults". */}
                                {' '}({row.adults_listed} adult{Number(row.adults_listed) === 1 ? '' : 's'})
                              </span>
                            </td>
                            <td style={{ fontSize: '0.9rem' }}>
                              {row.children_named || <span style={{ color: '#999' }}>none listed</span>}
                              {!includesThisStudent && (
                                <div style={{ color: '#8a5a12', fontSize: '0.8rem', marginTop: '0.15rem' }}>
                                  does not name this student
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                              {row.processed_at
                                ? `${new Date(row.processed_at).toLocaleDateString()}${row.processed_by_name ? ` — ${row.processed_by_name}` : ''}`
                                : <span style={{ color: '#999' }}>No</span>}
                            </td>
                            <td className="actions">
                              <button type="button" className="btn-secondary" onClick={() => openSchoolLunchPdf(row.id)}>
                                View PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {forms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ fontSize: '1.1em', color: '#666', marginBottom: '0.5rem' }}>
                    No forms submitted for this student yet.
                  </p>
                  <p style={{ fontSize: '0.9em', color: '#999' }}>
                    Forms will appear here once the parent completes and submits enrollment forms.
                  </p>
                </div>
              ) : (
                <div className="forms-list">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Form Name</th>
                        <th>Submitted</th>
                        <th>Parent</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forms.map(form => (
                        <tr key={form.id}>
                          <td>{form.instance_name || form.master_form_name}</td>
                          <td>{form.submitted_at ? new Date(form.submitted_at).toLocaleDateString() : new Date(form.created_at).toLocaleDateString()}</td>
                          <td>{form.parent_first_name} {form.parent_last_name}</td>
                          <td className="actions">
                            <button 
                              onClick={() => handleViewForm(form.id)} 
                              className="btn-edit"
                            >
                              View
                            </button>
                            <button 
                              onClick={() => {
                                setShowDeleteConfirm(form.id);
                              }} 
                              className="btn-danger"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : viewMode === 'pdf' ? (
            <PDFView 
              form={selectedForm}
              formDefinition={editFormData?.formDefinition}
              formValues={selectedForm.form_data}
              onClose={() => setViewMode('view')}
            />
          ) : (
            <div className="form-view-container">
              <div className="form-view-header">
                <h4>{selectedForm.instance_name || selectedForm.master_form_name}</h4>
                <div className="form-view-actions">
                  {viewMode === 'edit' ? (
                    <>
                      <button 
                        onClick={handleSaveEdit} 
                        className="btn-primary"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button 
                        onClick={handleCancelEdit} 
                        className="btn-secondary"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        onClick={handleViewPDF} 
                        className="btn-primary"
                      >
                        View as PDF
                      </button>
                      <button 
                        onClick={handleEditForm} 
                        className="btn-secondary"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => {
                          setShowDeleteConfirm(selectedForm.id);
                        }} 
                        className="btn-danger"
                      >
                        Delete
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedForm(null);
                          setViewMode(null);
                        }} 
                        className="btn-secondary"
                      >
                        Back to List
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="form-view-content">
                {editFormData?.formDefinition ? (
                  <FormRenderer
                    formData={editFormData.formDefinition}
                    formValues={editFormData.formValues}
                    onChange={handleFormChange}
                    readOnly={viewMode !== 'edit'}
                    fieldAmendments={editFormData.amendmentMetadata || {}}
                  />
                ) : (
                  <div>Loading form...</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
            <div className="modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Delete</h3>
                <button onClick={() => setShowDeleteConfirm(null)} className="btn-close">&times;</button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this form submission?</p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>
                  The form will be moved to trash and permanently deleted after 7 days.
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={() => handleDeleteForm(showDeleteConfirm)} 
                  className="btn-danger"
                >
                  Delete
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <SuccessPopup
      show={!!actionFeedback}
      onClose={() => setActionFeedback(null)}
      variant={actionFeedback?.variant || 'success'}
      title={actionFeedback?.title}
      message={actionFeedback?.message || ''}
      autoCloseDelay={actionFeedback?.variant === 'error' ? 0 : 5000}
    />
    </>
  );
};

export default StudentFormsModal;

